import { brands } from '@estimating/branding';
import { noopErrorReporter, type ErrorReporter } from '@estimating/error-reporting';
import { createI18nInstance, supportedLocales } from '@estimating/i18n';
import { QueryClient } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/components/App';
import { ConfigErrorScreen } from './app/components/ConfigErrorScreen';
import { createLocalePreferenceStore, resolveInitialLocale } from './app/locale/locale-preference';
import { rootErrorHooks } from './app/root-error-hooks';
import { createAppRouter } from './app/router';
import { loadRuntimeConfig, showsDeveloperTools } from './app/runtime-config';
import { initSentry } from './app/sentry';
import type { Authenticator } from './features/auth/authenticator';
import { createFirebaseAuthenticator } from './features/auth/firebase-authenticator';
import { createSingleSignOn, withSingleSignOn } from './features/auth/single-sign-on';
import { createApiClient } from './shared/api/api-client';

/** The stamp's config is missing or unusable. Without it there is no DSN, so the reason can only be logged. */
async function renderConfigError(container: HTMLElement, reason: unknown): Promise<void> {
  console.error('Runtime configuration unavailable', reason);
  const messages = await Promise.all(
    supportedLocales.map(async (lang) => ({
      lang,
      text: (await createI18nInstance(lang)).t('app.config_error'),
    })),
  );
  createRoot(container).render(<ConfigErrorScreen messages={messages} />);
}

async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('The page has no #root element');
  }

  const loaded = await loadRuntimeConfig();
  if (!loaded.ok) {
    await renderConfigError(container, loaded.reason);
    return;
  }

  const { config } = loaded;
  // The router needs the authenticator and Sentry needs the router, so the
  // authenticator reports through this until Sentry has started, a few lines down.
  let errorReporter: ErrorReporter = noopErrorReporter;
  const reportLater: ErrorReporter = {
    captureException: (error, context) => {
      errorReporter.captureException(error, context);
    },
    flush: (timeoutMs) => errorReporter.flush(timeoutMs),
  };
  let firebase: Authenticator;
  try {
    firebase = createFirebaseAuthenticator(config.firebase, reportLater);
  } catch (error) {
    // Firebase rejects some malformed project configs as soon as it starts.
    await renderConfigError(container, error);
    return;
  }
  // Single sign-on with the brand's shop management system, where the stamp allows it (D-060).
  const sso =
    config.sso &&
    createSingleSignOn(
      firebase,
      {
        apiBaseUrl: config.apiBaseUrl,
        projectId: config.firebase.projectId,
        cookieDomain: config.sso.cookieDomain,
      },
      reportLater,
    );
  const authenticator = sso ? withSingleSignOn(firebase, sso) : firebase;
  const router = createAppRouter(authenticator);
  // Started before the first render, so errors from then on are reported.
  errorReporter = initSentry(config, router);
  const root = createRoot(container, rootErrorHooks(errorReporter));

  const brand = brands[config.brand];
  const preferences = createLocalePreferenceStore();
  const initialLocale = resolveInitialLocale(brand, preferences.read());
  // A session kept from an earlier visit is restored before the first render, so
  // nothing shows as signed out for a moment.
  const [i18n] = await Promise.all([createI18nInstance(initialLocale), authenticator.ready()]);
  document.title = brand.productName;
  // From now on, a sign-in or sign-out in the platform shows here too.
  sso?.watch();

  root.render(
    <StrictMode>
      <App
        router={router}
        showDevtools={showsDeveloperTools(config)}
        brand={brand}
        i18n={i18n}
        initialLocale={initialLocale}
        preferences={preferences}
        apiClient={createApiClient(config.apiBaseUrl, { idToken: authenticator.idToken })}
        authenticator={authenticator}
        queryClient={new QueryClient()}
      />
    </StrictMode>,
  );
}

void bootstrap();
